import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Search, X, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../Dialog/Dialog'
import { INDICATOR_OPTIONS } from './catalog'
import type { IndicatorOption } from './types'

// TradingView-style indicators picker modal (same shell as the symbol search):
// title + close, a search field, and a flat list. Clicking a row ADDS the
// indicator and the modal stays open (multiple adds). Rows stay clickable when
// already added — multiple instances of the same indicator are supported (e.g.
// two MAs with different periods); the "Added" caption is just a hint. Volume
// (the workbench volume overlay) is the exception: single-instance, disabled
// once present.

export interface WorkbenchIndicatorSearchProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Names of the indicator instances already on the chart. */
  addedNames: string[]
  /** Whether the special Volume overlay is already present. */
  volumePresent: boolean
  onAdd: (option: IndicatorOption) => void
}

export function WorkbenchIndicatorSearch({
  open,
  onOpenChange,
  addedNames,
  volumePresent,
  onAdd,
}: WorkbenchIndicatorSearchProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-black/60"
        className="v1 bg-popover max-w-[560px] sm:max-w-[560px] w-[94vw] p-0 gap-0 overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-4 pt-4 pb-3">
          <DialogTitle className="text-section text-foreground">Indicators</DialogTitle>
        </DialogHeader>
        {/* Body lives in an inner component so the query/highlight state resets
            each time the dialog opens (Radix unmounts closed content). */}
        <SearchBody addedNames={addedNames} volumePresent={volumePresent} onAdd={onAdd} />
      </DialogContent>
    </Dialog>
  )
}

function SearchBody({
  addedNames,
  volumePresent,
  onAdd,
}: Pick<WorkbenchIndicatorSearchProps, 'addedNames' | 'volumePresent' | 'onAdd'>) {
  const [query, setQuery] = useState('')
  const [hiRaw, setHiRaw] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    return INDICATOR_OPTIONS.filter(
      (o) => !q || o.label.toLowerCase().includes(q) || o.name.toLowerCase().includes(q),
    )
  }, [query])

  // Clamp the highlight during render (instead of a state-sync effect).
  const hi = Math.min(hiRaw, Math.max(0, results.length - 1))

  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-row="${hi}"]`)
    node?.scrollIntoView({ block: 'nearest' })
  }, [hi])

  const isAdded = (o: IndicatorOption) =>
    addedNames.includes(o.name) || (o.volume === true && volumePresent)
  // Only Volume is single-instance; normal indicators can be added again.
  const isBlocked = (o: IndicatorOption) => o.volume === true && volumePresent

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHiRaw(Math.min(hi + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHiRaw(Math.max(hi - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const r = results[hi]
      if (r && !isBlocked(r)) onAdd(r) // stays open for multiple adds
    }
  }

  return (
    <>
      {/* search field */}
      <div className="px-4 pb-3">
        <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-border bg-muted/40 focus-within:border-primary transition-colors">
          <Search size={16} strokeWidth={1.75} className="text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setHiRaw(0)
            }}
            onKeyDown={onKeyDown}
            placeholder="Search"
            className="flex-1 bg-transparent text-body text-foreground placeholder:text-muted-foreground outline-none"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('')
                setHiRaw(0)
                inputRef.current?.focus()
              }}
              aria-label="Clear"
              className="inline-flex items-center justify-center h-5 w-5 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer shrink-0"
            >
              <X size={13} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {/* results */}
      <div ref={listRef} className="max-h-[52dvh] overflow-y-auto border-t border-border">
        {results.length === 0 ? (
          <div className="px-4 py-10 text-center text-body text-muted-foreground">
            No indicators match “{query}”.
          </div>
        ) : (
          results.map((o, i) => {
            const added = isAdded(o)
            const blocked = isBlocked(o)
            return (
              <button
                key={o.key}
                data-row={i}
                type="button"
                onMouseEnter={() => setHiRaw(i)}
                onClick={() => {
                  if (!blocked) onAdd(o)
                }}
                className={cn(
                  'group w-full flex items-center gap-3 px-4 h-11 text-left transition-colors cursor-pointer border-b border-border/50 last:border-b-0',
                  i === hi ? 'bg-muted/70' : 'hover:bg-muted/40',
                  blocked && 'opacity-50 pointer-events-none',
                )}
              >
                <span className="text-body text-foreground">{o.label}</span>
                <span className="text-caption text-muted-foreground">{o.name}</span>
                <span className="flex-1" />
                <span className="rounded bg-muted px-1.5 py-0.5 text-eyebrow uppercase tracking-wide text-muted-foreground">
                  {o.pane === 'main' ? 'overlay' : 'panel'}
                </span>
                {added && <span className="shrink-0 text-caption text-muted-foreground">Added</span>}
                {!blocked && (
                  <Plus
                    size={15}
                    strokeWidth={2}
                    className={cn(
                      'shrink-0 text-muted-foreground transition-opacity',
                      i === hi ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                )}
              </button>
            )
          })
        )}
      </div>
    </>
  )
}
